import React, { useState, useMemo } from "react";
import { services } from "../services";

type ContactType = "email" | "phone" | null;

const UpdatePassword: React.FC = () => {
  const [value, setValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [updatedPassword, setUpdatedPassword] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const contactType: ContactType = useMemo(() => {
    if (!value) return null;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    if (emailRegex.test(value)) return "email";
    if (phoneRegex.test(value)) return "phone";

    return null;
  }, [value]);

  const isPasswordMismatch =
    newPassword && confirmPassword && newPassword !== confirmPassword;

  const canSubmit =
    contactType &&
    newPassword.length >= 8 &&
    confirmPassword.length >= 8 &&
    !isPasswordMismatch &&
    !loading;

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!contactType) {
      setError("Please enter a valid email or mobile number");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    const payload = {
      ...(contactType === "email" ? { email: value } : { mobileNumber: value }),
      newPassword,
      confirmPassword,
    };

    try {
      const response = await services.requestSvc.post(
        "/update-password",
        payload
      );
      if (!response?.success) {
        throw new Error(response?.message || "Password update failed");
      }
      setUpdatedPassword(newPassword);
      setShowModal(true);
      setSuccess("Password updated successfully");
      setValue("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mt-10 bg-white p-6 rounded-xl shadow">
      <h2 className="text-xl font-semibold mb-2">Update Password</h2>

      <p className="text-sm text-gray-600 mb-4">
        Enter user's email/mobile and set a new password.
      </p>

      {/* Email / Mobile */}
      <input
        type="text"
        placeholder="Email or Mobile Number"
        value={value}
        onChange={(e) => {
          setValue(e.target.value.trim());
          setError("");
          setSuccess("");
        }}
        className="w-full border rounded-md px-3 py-2 mb-3 focus:ring-2 focus:ring-blue-500"
      />

      <input
        type="password"
        placeholder="New Password"
        value={newPassword}
        onChange={(e) => {
          setNewPassword(e.target.value);
          setError("");
        }}
        className="w-full border rounded-md px-3 py-2 mb-3 focus:ring-2 focus:ring-blue-500"
      />

      <input
        type="password"
        placeholder="Confirm New Password"
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setError("");
        }}
        className={`w-full border rounded-md px-3 py-2 mb-2 focus:ring-2 ${
          isPasswordMismatch
            ? "border-red-500 focus:ring-red-500"
            : "focus:ring-blue-500"
        }`}
      />

      {isPasswordMismatch && (
        <p className="text-sm text-red-600 mb-2">Passwords do not match</p>
      )}

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      {success && <p className="text-sm text-green-600 mt-2">{success}</p>}

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className={`mt-4 w-full py-2 rounded-md text-white font-medium
          ${!canSubmit ? "bg-gray-400 cursor-not-allowed" : "btn-primary"}`}
      >
        {loading ? "Updating..." : "Update Password"}
      </button>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-green-600 mb-2">
              Password Changed Successfully
            </h3>

            <p className="text-sm text-gray-600 mb-3">
              Please copy and share the password securely.
            </p>

            <div className="bg-gray-100 rounded-md px-3 py-2 mb-4 flex items-center justify-between">
              <span className="font-mono text-sm break-all">
                {updatedPassword}
              </span>
            </div>

            <button
              onClick={() => {
                setShowModal(false);
                setUpdatedPassword("");
              }}
              className="w-full py-2 rounded-md btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatePassword;
